// Privacy guard -- the enforcement layer between a call site and the sinks.
//
// Three jobs:
//   1. Per-event whitelist: only keys declared in events.ts EVENT_SCHEMA survive.
//   2. Forbidden-key strip: raw payload (coordinates, free text, filenames,
//      element ids) is NEVER allowed through, even if a caller mistakenly puts
//      it on the whitelist -- these keys are dropped unconditionally.
//   3. Bucketization: raw counts/sizes on `*_bucket` keys are coarsened to
//      ranges so no exact value leaves the browser.

import { REVIEW_TRACE } from '@bim-pdf/core';
import { EVENT_SCHEMA, type EventName } from './events';

/**
 * Id-like keys that ARE allowed, each with the format its value must match.
 * trace_id is the drafter's intent trace id (review contract v7
 * trace.field): an opaque random id, not a markup / element id. A
 * value that does not match the contract pattern is dropped.
 */
const TRACE_KEYS: Record<string, RegExp> = {
    trace_id: new RegExp(REVIEW_TRACE.pattern),
};

/** Coarse count buckets -- see spec. */
export function bucketCount(n: number): string {
    if (!Number.isFinite(n) || n <= 0) return '0';
    if (n === 1) return '1';
    if (n <= 5) return '2-5';
    if (n <= 20) return '6-20';
    if (n <= 100) return '21-100';
    return '100+';
}

/** Megabyte buckets for file sizes. */
export function bucketMb(x: number): string {
    if (!Number.isFinite(x) || x <= 0) return '0';
    if (x < 1) return '<1';
    if (x <= 5) return '1-5';
    if (x <= 20) return '6-20';
    if (x <= 100) return '21-100';
    return '100+';
}

/**
 * Keys that must NEVER reach a sink, regardless of event. Covers raw geometry,
 * free text, filenames, and any element identifier. Matching is case-insensitive
 * and also catches obvious id-list variants (element_ids, linkedElementIds...).
 */
const FORBIDDEN_KEYS = new Set<string>([
    'points', 'bbox', 'coordinates', 'coords', 'x', 'y', 'geometry', 'outline',
    'text', 'subject', 'label', 'title', 'note', 'notes', 'comment', 'content',
    'filename', 'file_name', 'name', 'document_name', 'documentname', 'email',
    'element_id', 'elementid', 'element_ids', 'elementids',
    'linked_element_ids', 'linkedelementids', 'markup_id', 'markupid',
    // Error string keys -- must never carry raw error messages, stacks, or details.
    'message', 'msg', 'stack', 'reason', 'error', 'error_message', 'detail', 'description',
]);

function isForbidden(key: string): boolean {
    const k = key.toLowerCase();
    if (FORBIDDEN_KEYS.has(k)) return true;
    // Defense in depth: anything that looks like an id or id-list is blocked.
    if (k.endsWith('_id') || k.endsWith('_ids') || k.endsWith('id') || k.endsWith('ids')) {
        // Allow explicit bucketed dimensions that legitimately end in nothing id-like;
        // none of our whitelist keys end in id/ids, so this is safe.
        return true;
    }
    return false;
}

/**
 * Filter + bucketize caller-supplied props for a given event.
 * - drops any key not on the event's whitelist
 * - drops any forbidden key (belt and suspenders)
 * - bucketizes numeric values on `*_bucket` / `*_bucket_mb` keys
 * - drops undefined values
 */
export function scrubProps(
    event: EventName,
    props: Record<string, unknown> | undefined,
): Record<string, string | number | boolean> {
    const out: Record<string, string | number | boolean> = {};
    if (!props) return out;

    const whitelist = EVENT_SCHEMA[event] as readonly string[];

    for (const [key, value] of Object.entries(props)) {
        if (value === undefined || value === null) continue;
        if (!whitelist.includes(key)) continue;
        const tracePattern = TRACE_KEYS[key];
        if (tracePattern) {
            if (typeof value === 'string' && tracePattern.test(value)) out[key] = value;
            continue;
        }
        if (isForbidden(key)) continue;

        // Bucketize raw numbers on bucket-typed keys so exact values never leave.
        if (typeof value === 'number') {
            if (key.endsWith('_bucket_mb') || key.endsWith('_mb')) {
                out[key] = bucketMb(value);
                continue;
            }
            if (key.endsWith('_bucket')) {
                out[key] = bucketCount(value);
                continue;
            }
            out[key] = value;
            continue;
        }

        if (typeof value === 'string' || typeof value === 'boolean') {
            out[key] = value;
        }
        // Objects/arrays are never allowed through.
    }

    return out;
}

/**
 * Remote opt-out check. When true, the remote (PostHog) sink is skipped; the
 * local debug sink still receives the event so local vetting keeps working.
 * Honors: navigator.doNotTrack === '1', navigator.globalPrivacyControl, localStorage 'pmt_opt_out' === '1',
 * or a `?optOut=1` query param.
 */
export function isRemoteOptedOut(): boolean {
    if (typeof navigator !== 'undefined' && navigator.doNotTrack === '1') return true;
    // Global Privacy Control (the opt-out signal US state privacy laws honor).
    if (typeof navigator !== 'undefined' && (navigator as { globalPrivacyControl?: unknown }).globalPrivacyControl === true) return true;
    if (typeof window !== 'undefined') {
        try {
            if (window.localStorage.getItem('pmt_opt_out') === '1') return true;
        } catch {
            // localStorage may throw in private mode -- ignore.
        }
        try {
            if (new URLSearchParams(window.location.search).get('optOut') === '1') return true;
        } catch {
            // ignore malformed URL
        }
    }
    return false;
}
